import { AlertType } from './../models/alert.model';
// Angular
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
// Partials for CRUD
import {
  ActionNotificationComponent,
  DeleteEntityDialogComponent,
  FetchEntityDialogComponent,
  UpdateStatusDialogComponent,
  ConfirmEntityDialogComponent
} from '../content/crud';
import { TranslateService } from '@ngx-translate/core';

export enum MessageType {
  Create,
  Read,
  Update,
  Delete,
  Success,
  Error,
  Warn,
}

@Injectable()
export class LayoutUtilsService {
  /**
   * Service constructor
   *
   * @param snackBar: MatSnackBar
   * @param dialog: MatDialog
   */
  constructor(private snackBar: MatSnackBar,
    private dialog: MatDialog, private translateService: TranslateService) { }


  showActionNotification(
    _message: string,
    _type: MessageType = MessageType.Create,
    _duration: number = 10000,
    _showCloseButton: boolean = true,
    _showUndoButton: boolean = true,
    _undoButtonDuration: number = 3000,
    _verticalPosition: 'top' | 'bottom' = 'top',
    _horizontalPosition: 'start' | 'center' | 'left' | 'right' | 'end' = 'center',
  ) {
    const _data = {
      message: _message,
      snackBar: this.snackBar,
      showCloseButton: _showCloseButton,
      showUndoButton: _showUndoButton,
      undoButtonDuration: _undoButtonDuration,
      horizontalPosition: _horizontalPosition,
      verticalPosition: _verticalPosition,
      type: _type,
      action: 'Undo'
    };
    return this.snackBar.openFromComponent(ActionNotificationComponent, {
      duration: _duration,
      data: _data,
      horizontalPosition: _horizontalPosition,
      verticalPosition: _verticalPosition
    });
  }

  showNotification(
    _message: string,
    _type: MessageType = MessageType.Create,
    _duration: number = 10000,
    _showCloseButton: boolean = true,
    _showUndoButton: boolean = false,
    _undoButtonDuration: number = 3000,
    _verticalPosition: 'top' | 'bottom' = 'top',
    _horizontalPosition: 'start' | 'center' | 'left' | 'right' | 'end' = 'center',
  ) {
    const _data = {
      message: this.translateService.instant(_message),
      snackBar: this.snackBar,
      showCloseButton: _showCloseButton,
      showUndoButton: _showUndoButton,
      undoButtonDuration: _undoButtonDuration,
      verticalPosition: _verticalPosition,
      type: _type,
      action: 'Undo'
    };

    const stylePanel = MessageType.Error === _type ? ['error'] : MessageType.Warn === _type ? ['warning'] : ['success'];

    return this.snackBar.openFromComponent(ActionNotificationComponent, {
      duration: _duration,
      data: _data,
      panelClass: ['notification', ...stylePanel],
      verticalPosition: _verticalPosition
    });
  }


  deleteElement(title: string = '', description: string = '', waitDesciption: string = '') {
    return this.dialog.open(DeleteEntityDialogComponent, {
      data: { title, description, waitDesciption },
      panelClass: 'confirm-modal',
      width: '440px'
    });
  }


  customConfirmationElement(
    title: string = '',
    description: string = '',
    waitDesciption: string = '',
    confirmButton: string = 'buttons.confirm',
    innerHtml: boolean = false,
    cancelBtnLabel: string = 'buttons.cancel') {
    return this.dialog.open(ConfirmEntityDialogComponent, {
      data: { title, description, waitDesciption, confirmButton, cancelBtnLabel, showYesBtn: true, innerHtml },
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'confirm-modal',
      scrollStrategy: new NoopScrollStrategy(),
      width: '440px',
      autoFocus: 'first-tabbable'
    });
  }

  customSuccessConfirmationElement(
    title: string = '',
    description: string = '',
    waitDesciption: string = '',
    confirmButton: string = 'Confirm',
    innerHtml: boolean = false) {
    return this.dialog.open(ConfirmEntityDialogComponent, {
      data: { title, description, waitDesciption, confirmButton, showYesBtn: true, innerHtml, alertType: AlertType.SUCCESS },
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'confirm-modal',
      scrollStrategy: new NoopScrollStrategy(),
      width: '440px',
      autoFocus: 'first-tabbable'
    });
  }

  showMessageDialogElement(title: string = '', description: string = '', btnLabel: string = 'Ok', isInnerHtml = false) {
    return this.dialog.open(ConfirmEntityDialogComponent, {
      data: { title, description, singleBtnLabel: btnLabel, singleBtn: true, showYesBtn: false, innerHtml: isInnerHtml },
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'confirm-modal',
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px'
    });
  }

  showErrorMessageDialogElement(title: string = '', description: string = '', btnLabel: string = 'Ok', isInnerHtml = false) {
    return this.dialog.open(ConfirmEntityDialogComponent, {
      data: { title, description, singleBtnLabel: btnLabel, singleBtn: true, showYesBtn: false, innerHtml: isInnerHtml, alertType: AlertType.ERROR },
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'confirm-modal',
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px'
    });
  }

  showWarningMessageDialogElement(title: string = '', description: string = '', btnLabel: string = 'Ok', isInnerHtml = false) {
    return this.dialog.open(ConfirmEntityDialogComponent, {
      data: { title, description, singleBtnLabel: btnLabel, singleBtn: true, showYesBtn: false, innerHtml: isInnerHtml, alertType: AlertType.WARNING },
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'confirm-modal',
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px'
    });
  }

  showSuccessMessageDialogElement(title: string = '', description: string = '', btnLabel: string = 'Ok', isInnerHtml = false) {
    return this.dialog.open(ConfirmEntityDialogComponent, {
      data: { title, description, singleBtnLabel: btnLabel, singleBtn: true, showYesBtn: false, innerHtml: isInnerHtml, alertType: AlertType.SUCCESS },
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'confirm-modal',
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px'
    });
  }

  showInfoMessageDialogElement(title: string = '', description: string = '', btnLabel: string = 'buttons.ok', isInnerHtml = false) {
    return this.dialog.open(ConfirmEntityDialogComponent, {
      data: { title, description, singleBtnLabel: btnLabel, singleBtn: true, showYesBtn: false, innerHtml: isInnerHtml, alertType: AlertType.INFO },
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'confirm-modal',
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px'
    });
  }

  confirmationElement(title: string = '', description: string = '', waitDesciption: string = '') {
    return this.dialog.open(DeleteEntityDialogComponent, {
      data: { title, description, waitDesciption },
      width: '440px'
    });
  }

  fetchElements(_data: any) {
    return this.dialog.open(FetchEntityDialogComponent, {
      data: _data,
      panelClass: 'confirm-modal',
      width: '400px'
    });
  }

  updateStatusForEntities(title: any, statuses: any, messages: any) {
    return this.dialog.open(UpdateStatusDialogComponent, {
      data: { title, statuses, messages },
      panelClass: 'confirm-modal',
      width: '480px'
    });
  }

}
